import React, { useState } from 'react';
import { Language, t } from '../data/translations';
import { GameState, GamePhase, Player, HeroId, GameAction } from '../types';
import GameModal from './GameModal';
import VictoryPointAssignModal from './VictoryPointAssignModal';
import { getApiBase } from '../services/apiBase';
import { fetchHeros } from '../services/okMainApi';
import * as gameLogic from '../services/gameLogic';

interface GameOverFlowProps {
    gameState: GameState;
    localPlayerId: number;
    isMultiplayer: boolean;
    auth: { token?: string } | null;
    onNavigate: (screen: string) => void;
    onRestart: () => void;
    setPlayerIsReady: (ready: boolean) => void;
    setGameState: React.Dispatch<React.SetStateAction<GameState | null>>;
    executeGameAction: (action: GameAction | any, optimisticApply: (state: GameState) => GameState) => void;
    setAvailableHeroes: (heroes: HeroId[]) => void;
    language: Language;
}

const GameOverFlow: React.FC<GameOverFlowProps> = ({
    gameState,
    localPlayerId,
    isMultiplayer,
    onNavigate,
    onRestart,
    setPlayerIsReady,
    setGameState,
    executeGameAction,
    setAvailableHeroes,
    auth,
    language
}) => {
    const [fameAcknowledged, setFameAcknowledged] = useState(false);

    if (gameState.phase !== GamePhase.GameOver) return null;

    const pv = gameState.pendingVictory;
    const isWin = pv && pv.winnerId === Number(localPlayerId) && isMultiplayer && !gameState.players.some(p => p.isAI);
    const isTechnical = pv?.isTechnical === true;
    const canAssignPoint = pv?.canAssignPoint !== false; // default true for normal wins
    const fameDelta = pv?.reportedFameDelta ?? gameState.lastFameDelta ?? 0;

    return (
        <>
            {/* Step 1: Fame Summary for multiplayer winners (shown before World Map) */}
            {isWin && !fameAcknowledged && (
                <GameModal
                    variant="victory"
                    title={
                        isTechnical
                            ? t('ui.technicalVictory', language)
                            : t('ui.victory', language)
                    }
                    message={
                        fameDelta > 0
                            ? t('ui.fameSummary', language, { fame: String(fameDelta) })
                            : t('ui.fameSummaryNone', language)
                    }
                    onRestart={() => {
                        if (canAssignPoint) {
                            setFameAcknowledged(true);
                        } else {
                            // No map point — go straight to lobby
                            setPlayerIsReady(false);
                            onNavigate('ROOM_LOBBY');
                        }
                    }}
                    language={language}
                />
            )}

            {/* Step 2: Victory Point Assignment for Multiplayer Winner (after fame acknowledged) */}
            {isWin && fameAcknowledged && canAssignPoint && (
                <VictoryPointAssignModal
                    gameState={gameState}
                    language={language}
                    onAssign={async (zoneId) => {
                        const storedFactionId = localStorage.getItem('factionId');
                        const myFaction = storedFactionId ? parseInt(storedFactionId) : 0;

                        try {
                            const base = getApiBase();
                            const token = auth?.token;

                            if (!token) {
                                console.error('[App] NO TOKEN! Cannot call API. Auth state:', auth);
                            }
                            if (myFaction === 0) {
                                console.error('[App] NO FACTION! factionId not found in localStorage');
                            }

                            if (token && myFaction > 0) {
                                fetch(`${base}/api/zone/capture`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ token, zoneId, factionId: myFaction })
                                })
                                    .then(r => r.json())
                                    .then(async data => {
                                        if (data.ok) {
                                            let basePoints = { harmonized: 0, chaotic: 0, untouched: 0 };
                                            if (data.zone) {
                                                basePoints = {
                                                    harmonized: data.zone.point1 || 0,
                                                    chaotic: data.zone.point2 || 0,
                                                    untouched: data.zone.point3 || 0
                                                };
                                            }

                                            executeGameAction(
                                                {
                                                    type: 'ASSIGN_VICTORY_POINT',
                                                    playerId: Number(localPlayerId),
                                                    zoneId,
                                                    factionId: myFaction,
                                                    basePoints
                                                } as any,
                                                (state) => gameLogic.assignVictoryPoint(state, Number(localPlayerId), zoneId, myFaction, basePoints)
                                            )

                                            const meResp = await fetch(`${base}/api/me?token=${encodeURIComponent(token)}`);
                                            const meData = await meResp.json();
                                            if (meData.ok && meData.user && meData.user.heroes) {
                                                const serverHeroes: string[] = meData.user.heroes;
                                                const allHeroes = await fetchHeros();
                                                const capturedIds: number[] = [];
                                                if (Array.isArray(allHeroes)) {
                                                    serverHeroes.forEach(serverHeroName => {
                                                        const match = allHeroes.find((h: any) => {
                                                            const hName = (h.name || '').toUpperCase().replace(/[\s-]/g, '_');
                                                            const sName = serverHeroName.toUpperCase().replace(/[\s-]/g, '_');
                                                            return hName === sName;
                                                        });
                                                        if (match) capturedIds.push(match.id);
                                                    });
                                                }
                                                if (capturedIds.length > 0) {
                                                    localStorage.setItem('capturedHeroes', JSON.stringify(capturedIds));
                                                    localStorage.setItem('capturedHeroes_p_1', JSON.stringify(capturedIds));
                                                    const newUnlocked: HeroId[] = [];
                                                    allHeroes.forEach(h => {
                                                        if (capturedIds.includes(h.id)) {
                                                            const name = h.name.toUpperCase().replace(/[\s-]/g, '_') as HeroId;
                                                            if (!newUnlocked.includes(name)) newUnlocked.push(name);
                                                        }
                                                    });
                                                    setAvailableHeroes(newUnlocked);
                                                }
                                            }
                                        } else {
                                            console.error('[App] Server returned error:', data);
                                            executeGameAction(
                                                { type: 'ASSIGN_VICTORY_POINT', playerId: Number(localPlayerId), zoneId, factionId: myFaction } as any,
                                                (state) => gameLogic.assignVictoryPoint(state, Number(localPlayerId), zoneId, myFaction)
                                            )
                                        }
                                    })
                                    .catch(e => {
                                        console.error('[App] Failed to report capture:', e);
                                        executeGameAction(
                                            { type: 'ASSIGN_VICTORY_POINT', playerId: Number(localPlayerId), zoneId, factionId: myFaction } as any,
                                            (state) => gameLogic.assignVictoryPoint(state, Number(localPlayerId), zoneId, myFaction)
                                        )
                                    });
                            } else {
                                executeGameAction(
                                    { type: 'ASSIGN_VICTORY_POINT', playerId: Number(localPlayerId), zoneId, factionId: myFaction } as any,
                                    (state) => gameLogic.assignVictoryPoint(state, Number(localPlayerId), zoneId, myFaction)
                                )
                            }
                        } catch (e) {
                            console.error('[App] Error in capture flow:', e);
                            executeGameAction(
                                { type: 'ASSIGN_VICTORY_POINT', playerId: Number(localPlayerId), zoneId, factionId: myFaction } as any,
                                (state) => gameLogic.assignVictoryPoint(state, Number(localPlayerId), zoneId, myFaction)
                            )
                        }
                    }}
                />
            )}

            {/* Standard Game Over Modal (loss, draw, AI game, or multiplayer defeat) */}
            {!isWin && (
                <GameModal
                    variant={gameState.winner?.id === Number(localPlayerId) ? 'victory' : 'default'}
                    title={
                        !gameState.winner ? t('ui.draw', language) :
                            gameState.winner.id === Number(localPlayerId) ? t('ui.victory', language) :
                                t('ui.defeat', language)
                    }
                    message={
                        !gameState.winner ? t('ui.drawMsg', language) :
                            gameState.winner.id === Number(localPlayerId)
                                ? (gameState.players.some(p => p.isAI)
                                    ? t('ui.victorySolo', language, { name: gameState.winner.name })
                                    : t('ui.victoryMsg', language, {
                                        name: gameState.winner.name,
                                        fame: String(gameState.lastFameDelta || 0)
                                    }))
                                : t('ui.defeatMsg', language, { name: gameState.winner.name })
                    }
                    restartLabel={
                        (gameState.winner?.id === Number(localPlayerId) && gameState.players.some(p => p.isAI))
                            ? t('ui.nextGame', language)
                            : undefined
                    }
                    onToMenu={!isMultiplayer ? () => onNavigate('MAIN_MENU') : undefined}
                    onRestart={() => {
                        if (isMultiplayer) {
                            setPlayerIsReady(false);
                            onNavigate('ROOM_LOBBY');
                        } else {
                            onRestart();
                        }
                    }}
                    language={language}
                />
            )}

        </>
    );
};

export default GameOverFlow;
